// Admin → System Health dashboard. Read-only operational view: KPI tiles for
// the last 24h + a tabbed log viewer that surfaces webhooks, payments,
// booking-status transitions, API health pings, failovers, emails, audit
// events and OTPs. Wires to /api/admin/system-health/*.
import React, { useEffect, useState } from 'react';
import { Loader2, Activity, AlertCircle, RefreshCw } from 'lucide-react';
import { api } from '../../lib/api';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';

const Tile = ({ label, value, accent = 'gray', sub, testid }) => (
  <Card className="bg-white dark:bg-gray-900 border-gray-200 dark:border-gray-800" data-testid={testid}>
    <CardContent className="p-4">
      <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">{label}</p>
      <p className={`mt-1 text-2xl font-bold text-${accent}-700 dark:text-${accent}-300`}>{value}</p>
      {sub && <p className="text-[11px] text-gray-500 mt-0.5">{sub}</p>}
    </CardContent>
  </Card>
);

const LOG_KINDS = [
  { key: 'webhooks',       label: 'Webhooks',         desc: 'Razorpay event log' },
  { key: 'payments',       label: 'Payments',         desc: 'Captured / failed transactions' },
  { key: 'booking_status', label: 'Booking Status',   desc: 'State transition trail' },
  { key: 'api_health',     label: 'API Health',       desc: 'Supplier test-connection pings' },
  { key: 'failover',       label: 'Failover',         desc: 'Supplier retry attempts' },
  { key: 'emails',         label: 'Emails',           desc: 'Resend / mock send log' },
  { key: 'audit',          label: 'Audit',            desc: 'Admin actions' },
  { key: 'otp',            label: 'OTP',              desc: 'MSG91 / Resend dispatch' },
];

const AdminSystemHealth = () => {
  const [overview, setOverview] = useState(null);
  const [funnel, setFunnel] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeLog, setActiveLog] = useState('webhooks');
  const [logs, setLogs] = useState([]);
  const [logLoading, setLogLoading] = useState(false);

  const loadOverview = async () => {
    setLoading(true);
    try {
      const [o, f] = await Promise.all([
        api.get('/admin/system-health/overview'),
        api.get('/admin/system-health/booking-funnel'),
      ]);
      setOverview(o.data);
      setFunnel(f.data?.funnel || []);
    } finally {
      setLoading(false);
    }
  };

  const loadLog = async (kind) => {
    setLogLoading(true);
    try {
      const r = await api.get(`/admin/system-health/logs/${kind}`);
      setLogs(r.data?.items || []);
    } finally {
      setLogLoading(false);
    }
  };

  useEffect(() => { loadOverview(); }, []);
  useEffect(() => { loadLog(activeLog); }, [activeLog]);

  return (
    <div className="space-y-6" data-testid="admin-system-health-page">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <Activity className="h-6 w-6 text-red-600" />
            System Health
          </h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Last 24 hours · Webhooks, payments, supplier reliability, audit trail.
          </p>
        </div>
        <Button size="sm" variant="outline" onClick={loadOverview} data-testid="health-refresh-btn">
          <RefreshCw className="h-3.5 w-3.5 mr-1.5" /> Refresh
        </Button>
      </div>

      {/* KPI tiles */}
      {loading || !overview ? (
        <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-red-600" /></div>
      ) : (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
            <Tile testid="health-tile-bookings-total" label="Bookings Total" value={overview.bookings.total} accent="gray" />
            <Tile testid="health-tile-bookings-pending" label="Pending" value={overview.bookings.pending} accent="amber" />
            <Tile testid="health-tile-bookings-confirmed" label="Confirmed" value={overview.bookings.confirmed} accent="emerald" />
            <Tile testid="health-tile-bookings-failed" label="Failed" value={overview.bookings.failed} accent="red" />
            <Tile testid="health-tile-bookings-cancelled" label="Cancelled / Refunded" value={overview.bookings.cancelled} accent="blue" />
            <Tile testid="health-tile-invoices" label="Invoices (24h)" value={overview.invoices_24h} accent="red" />
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
            <Tile testid="health-tile-payments" label="Payments (24h)" value={overview.payments_24h.received}
                  sub={overview.payments_24h.success_rate != null ? `Success ${overview.payments_24h.success_rate}%` : '—'} accent="emerald" />
            <Tile testid="health-tile-payments-failed" label="Payment Failures" value={overview.payments_24h.failed} accent="red" />
            <Tile testid="health-tile-webhooks" label="Webhooks (24h)" value={overview.webhooks_24h} accent="gray" />
            <Tile testid="health-tile-api-fail" label="API Health Fails" value={overview.api_health_failures_24h} accent="amber" />
            <Tile testid="health-tile-failover" label="Failovers (24h)" value={overview.failover_attempts_24h} accent="blue" />
            <Tile testid="health-tile-emails" label="Emails (24h)" value={`${overview.emails_24h.sent} / ${overview.emails_24h.failed} fail`} accent="gray" />
          </div>

          {/* Booking funnel */}
          <Card className="bg-white dark:bg-gray-900 border-gray-200 dark:border-gray-800">
            <CardContent className="p-4">
              <h2 className="text-sm font-bold mb-3 text-gray-900 dark:text-white">Booking Funnel — last 24h</h2>
              {funnel.length === 0 ? (
                <p className="text-sm text-gray-500 italic">No status transitions in the last 24 hours.</p>
              ) : (
                <div className="flex flex-wrap gap-2">
                  {funnel.map((f) => (
                    <div key={f.status} className="flex items-center gap-2 px-3 py-1.5 rounded-md bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700">
                      <Badge className="uppercase text-[10px] bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300">{f.status}</Badge>
                      <span className="font-bold text-gray-900 dark:text-white">{f.count}</span>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </>
      )}

      {/* Log viewer */}
      <Card className="bg-white dark:bg-gray-900 border-gray-200 dark:border-gray-800">
        <CardContent className="p-0">
          <div className="flex overflow-x-auto border-b border-gray-200 dark:border-gray-800">
            {LOG_KINDS.map((k) => (
              <button key={k.key}
                onClick={() => setActiveLog(k.key)}
                data-testid={`health-tab-${k.key}`}
                className={`px-4 py-3 text-xs font-semibold whitespace-nowrap transition-colors ${
                  activeLog === k.key
                    ? 'border-b-2 border-red-600 text-red-700 dark:text-red-300'
                    : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                }`}>
                {k.label}
              </button>
            ))}
          </div>
          <div className="p-4">
            <p className="text-xs text-gray-500 mb-3">
              {LOG_KINDS.find((k) => k.key === activeLog)?.desc || ''}
            </p>
            {logLoading ? (
              <div className="flex justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-red-600" /></div>
            ) : logs.length === 0 ? (
              <div className="text-center py-8 text-sm text-gray-500">
                <AlertCircle className="h-8 w-8 mx-auto mb-2 text-gray-300" />
                No entries in this log yet.
              </div>
            ) : (
              <div className="overflow-x-auto" data-testid={`health-log-table-${activeLog}`}>
                <pre className="text-[11px] leading-tight bg-gray-50 dark:bg-gray-950 text-gray-700 dark:text-gray-300 p-3 rounded max-h-[480px] overflow-auto border border-gray-200 dark:border-gray-800">
{logs.slice(0, 50).map((row, i) => JSON.stringify(row, null, 2)).join('\n\n')}
                </pre>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminSystemHealth;
